import { useCallback, useEffect, useMemo, useRef } from 'react';

import { PermissionType } from 'api/types/entity';
import { ContentHeader } from 'components';
import { config } from 'config';
import { lodash } from 'helpers';
import { useFormErrorFocus, useFormPrompt } from 'hooks';
import { useFormContext } from 'libs/form/useFormContext';
import { useTranslation } from 'libs/i18n';
import { usePermissions } from 'libs/permissions';
import { Stack } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';

import { ReasonChangeFormType } from '../../forms/reasonChangeForm';
import { DialogConfirmLinkToBright } from '../DialogConfirmLinkToBright';
import { DialogReasonChange } from '../DialogReasonChange';

import { Actions } from './Actions';
import { FormFooter } from './FormFooter';
import { ContentStyled } from './styles';
import { ActionType, ClientFormContentProps } from './types';

export const ClientFormContent = ({
  content,
  headerTitle,
  submitLoading,
  actionMenuProps,
  headerRightContent,
  headerAfterContent,
  resetForm,
  onEnableReadOnly,
  onEdit,
  onShowVersionHistory,
}: ClientFormContentProps) => {
  const { t } = useTranslation();
  const {
    isOpen: isOpenDialogReason,
    onClose: onCloseDialogReason,
    open: openDialogReason,
  } = useDialog();

  const {
    isOpen: isOpenDialogConfirmLinkToBright,
    onClose: onCloseDialogConfirmLinkToBright,
    open: openDialogConfirmLinkToBright,
  } = useDialog();

  const {
    readOnly,
    submitDisabled,
    dirty,
    handleSubmit,
    setFieldValue,
    validateForm,
  } = useFormContext<ReasonChangeFormType>();

  const { checkPermissions } = usePermissions();

  const { formErrorFocus } = useFormErrorFocus();

  const editable = !readOnly;

  const { confirmIsChanged } = useFormPrompt();

  const disableEditable = useCallback(() => {
    confirmIsChanged(() => onEnableReadOnly());
  }, [confirmIsChanged, onEnableReadOnly]);

  useEffect(() => {
    if (readOnly) {
      resetForm();
    }
  }, [readOnly, resetForm]);

  const editableRef = useRef(editable);

  useEffect(() => {
    editableRef.current = editable;
  }, [editable]);

  useEffect(
    () => () => {
      if (editableRef.current) {
        onEnableReadOnly();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const actions = useMemo(
    () =>
      lodash.compact([
        actionMenuProps?.onDownloadClientQuestionnaire &&
          checkPermissions([
            PermissionType.Client_LegalClient_ExportQuestionnaire,
          ]) && {
            type: ActionType.downloadClientQuestionnaire,
          },

        actionMenuProps?.onDelete && {
          type: ActionType.delete,
        },

        actionMenuProps?.onArchive && {
          type: ActionType.archive,
        },
        actionMenuProps?.onDearchive && {
          type: ActionType.dearchive,
        },

        actionMenuProps?.onSendInvitationToAccount && {
          type: ActionType.sendInvitationToAccount,
        },

        actionMenuProps?.onLinkToBright && {
          type: ActionType.linkToBright,
        },

        actionMenuProps?.onRevokeOtpVerification &&
          checkPermissions([
            PermissionType.Client_LegalClient_RevokeOtpVerification,
          ]) && {
            type: ActionType.revokeOtpVerification,
          },
        actionMenuProps?.onChangeOnboardingStatus && {
          type: ActionType.changeOnboardingStatus,
        },
        actionMenuProps?.onChangeAccountStatus && {
          type: ActionType.changeAccountStatus,
        },

        onShowVersionHistory &&
          checkPermissions([
            PermissionType.History_Client_LegalClient_GetOne,
          ]) && {
            type: ActionType.showVersionHistory,
          },
        actionMenuProps?.showEditAction !== false &&
          checkPermissions([PermissionType.Client_LegalClient_UpdateOne]) && {
            type: ActionType.edit,
          },
      ]),
    [actionMenuProps, onShowVersionHistory, checkPermissions]
  );

  const onClickAction = (action: ActionType) => {
    switch (action) {
      case ActionType.showVersionHistory:
        onShowVersionHistory?.();
        break;
      case ActionType.edit:
        onEdit();
        break;
      case ActionType.changeOnboardingStatus:
        actionMenuProps?.onChangeOnboardingStatus?.();
        break;
      case ActionType.changeAccountStatus:
        actionMenuProps?.onChangeAccountStatus?.();
        break;
      case ActionType.delete:
        actionMenuProps?.onDelete?.();
        break;
      case ActionType.archive:
        actionMenuProps?.onArchive?.();
        break;
      case ActionType.dearchive:
        actionMenuProps?.onDearchive?.();
        break;
      case ActionType.revokeOtpVerification:
        actionMenuProps?.onRevokeOtpVerification?.();
        break;
      case ActionType.sendInvitationToAccount:
        actionMenuProps?.onSendInvitationToAccount?.();
        break;
      case ActionType.linkToBright:
        openDialogConfirmLinkToBright();
        break;
      case ActionType.downloadClientQuestionnaire:
        actionMenuProps?.onDownloadClientQuestionnaire?.();
        break;
      default:
        break;
    }
  };

  const onSaveDialogReason = (reason: string) => {
    onCloseDialogReason();
    setFieldValue('reasonChange', { reason });
    // need to call with a delay so that the field has time to record
    setTimeout(handleSubmit, 0);
  };

  const onConfirmLinkToBright = () => {
    onCloseDialogConfirmLinkToBright();
    actionMenuProps?.onLinkToBright?.();
  };

  const handleOnSave = async () => {
    const validateResult = await validateForm();

    if (validateResult) {
      const invalidKeys = Object.keys(validateResult).filter(
        (v) => v !== 'reasonChange'
      );
      if (invalidKeys.length) {
        if (config.IS_DEVELOPMENT) {
          // eslint-disable-next-line no-console
          console.info(invalidKeys);
        }
        formErrorFocus();
        return;
      }
    }
    openDialogReason();
  };

  return (
    <Stack spacing={32}>
      <ContentHeader
        header={editable ? t('client.client.editTitle') : headerTitle}
        headerAfterContent={headerAfterContent}
        rightContent={
          <Stack alignItems="center" direction="row" spacing={12}>
            {headerRightContent}
            {!editable && actions.length > 0 && (
              <Actions actions={actions} onClickAction={onClickAction} />
            )}
          </Stack>
        }
      />

      <ContentStyled>{content}</ContentStyled>

      <DialogReasonChange
        isOpen={isOpenDialogReason}
        onClose={onCloseDialogReason}
        onSave={onSaveDialogReason}
      />

      <DialogConfirmLinkToBright
        isOpen={isOpenDialogConfirmLinkToBright}
        onClose={onCloseDialogConfirmLinkToBright}
        onConfirm={onConfirmLinkToBright}
      />

      <FormFooter
        editable={editable}
        submitDisabled={submitDisabled || !dirty}
        submitLoading={submitLoading}
        onCancel={disableEditable}
        onSubmit={handleOnSave}
      />
    </Stack>
  );
};
